import { useCallback, useEffect, useRef } from 'react';

import qapi from 'views_shared/app/javascript/api';
import qlog from 'views_shared/app/javascript/scripts/qlog';

/**
 * Inbound `?pitch=<v2 id>` URLs — the virtual-pitch-row counterpart of useDeepLinkResolver.
 *
 * Resolves on mount and on popstate. A pitch that has since gained a conversation resolves
 * to that conversation instead: the URL is rewritten to ?conversation= and re-dispatched
 * through the conversation deep-link machinery, so a stale pitch link lands on the thread
 * it became. Selection here never marks the pitch read (parity with conversation deep
 * links, whose auto-open doesn't mark read either). A pitch that isn't the viewer's (or is
 * gone) is ignored and the inbox loads normally.
 */
function usePitchDeepLink({
  currentReceiverProfileId,
  isMedia,
  activeMailbox,
  selectMailbox,
  selectedPitchRef,
  selectPitch,
  injectPitch,
}) {
  // One always-current snapshot of the inputs, so resolveFromCurrentUrl can stay dep-free
  // and the popstate listener never rebinds. (selectedPitchRef is already a ref — read it
  // through here directly rather than wrapping a ref in another ref.)
  const latest = useRef(null);
  latest.current = {
    currentReceiverProfileId,
    isMedia,
    activeMailbox,
    selectMailbox,
    selectedPitchRef,
    selectPitch,
    injectPitch,
  };

  const fetchGenerationRef = useRef(0);

  const resolveFromCurrentUrl = useCallback(() => {
    if (typeof window === 'undefined') return;

    const { currentReceiverProfileId: profileId, isMedia: media, selectedPitchRef: selectedRef } = latest.current;
    if (!profileId || !media) return;

    const params = new URLSearchParams(window.location.search);
    const pitchId = Number(params.get('pitch'));
    if (!pitchId) return;
    if (selectedRef?.current?.id === pitchId) return;

    const generation = fetchGenerationRef.current + 1;
    fetchGenerationRef.current = generation;

    (async () => {
      try {
        const [pitchResult, convResult] = await Promise.all([
          qapi.pitches.get(pitchId),
          qapi.conversations.getByPitchOriginIds(profileId, [pitchId]),
        ]);

        if (generation !== fetchGenerationRef.current) return;

        const conv = convResult?.data?.[0];
        if (conv) {
          // Converged: hand over to the conversation deep-link flow (handles mailbox +
          // trash placement). replaceState fires no popstate, so dispatch one ourselves.
          const rewritten = new URLSearchParams(window.location.search);
          rewritten.delete('pitch');
          rewritten.set('conversation', conv.conversation_id);
          window.history.replaceState({}, '', `${window.location.pathname}?${rewritten}${window.location.hash}`);
          window.dispatchEvent(new PopStateEvent('popstate'));
          return;
        }

        const pitch = pitchResult?.data?.[0];
        if (!pitch || pitch.request_media_person_id !== profileId) return;

        if (latest.current.activeMailbox !== 'everything') latest.current.selectMailbox('everything');
        // A pitch deeper than the stream's pagination needs its row injected to render.
        latest.current.injectPitch?.(pitch);
        latest.current.selectPitch(pitch);
      } catch (err) {
        qlog.error('Pitch deep-link resolver failed:', err);
      }
    })();
  }, []);

  useEffect(() => {
    resolveFromCurrentUrl();
  }, [currentReceiverProfileId, resolveFromCurrentUrl]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    window.addEventListener('popstate', resolveFromCurrentUrl);
    return () => window.removeEventListener('popstate', resolveFromCurrentUrl);
  }, [resolveFromCurrentUrl]);
}

export default usePitchDeepLink;
